import { useCallback, useEffect, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  InfoPage,
  isTouchDevice,
  Link,
  LogSentryException,
  TextField,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";

import useNavigation from "../../hooks/navigation";
import { useAuthMethod } from "../../hooks/useAuth";
import useTranslation from "../../hooks/useTranslation";
import { AuthError, AuthMethod, SearchParam } from "../../types";
import { FormWrapper } from "../FormWrapper/FormWrapper";
import Heading from "../Heading/Heading";
import ReCAPTCHA, { type ReCAPTCHAAPI } from "../ReCAPTCHA/ReCAPTCHA";

import style from "./ProjectViewLinkInvitationSignIn.module.css";

const signInSchema = z.object({
  email: z.string(),
  password: z.string().optional(),
});

type SignInFormData = z.infer<typeof signInSchema>;

interface ProjectViewLinkSignInProps {
  submit: (email: string, password: string, recaptcha?: string) => void;
  inProgress: boolean;
  loginError?: AuthError | null;
  recaptchaLoaded?: boolean;
  emailHint: string;
}

export const ProjectViewLinkInvitationSignIn = ({
  submit,
  inProgress,
  loginError,
  recaptchaLoaded,
  emailHint,
}: ProjectViewLinkSignInProps) => {
  const { t, m } = useTranslation();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const recaptchaRef = useRef<ReCAPTCHAAPI>(null);
  const passwordFieldRef = useRef<HTMLInputElement>(null);
  const [isCaptchaEnabled, setIsCaptchaEnabled] = useState(false);
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(recaptchaLoaded);

  const {
    control,
    handleSubmit,
    setError: setFormError,
    clearErrors,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: emailHint, password: "" },
  });

  const {
    mutate: mutateAuthMethod,
    data: authMethodData,
    isSuccess: isAuthMethodSuccess,
    isError: isAuthMethodError,
    isPending: isAuthMethodPending,
  } = useAuthMethod();

  const isAuthMethodSSO = authMethodData?.method === AuthMethod.SSO;
  const isAuthMethodPassword = authMethodData?.method === AuthMethod.Password;

  useEffect(() => {
    if (isAuthMethodSuccess && !isTouchDevice() && isAuthMethodPassword) {
      setTimeout(() => passwordFieldRef.current?.focus());
    }
  }, [isAuthMethodSuccess, isAuthMethodPassword]);

  useEffect(() => {
    switch (loginError) {
      case AuthError.InvalidEmailPassword:
        setFormError("root", { message: t(m.Login.PasswordIncorrect) });
        break;
      case AuthError.RateLimit:
        setFormError("root", { message: t(m.ErrorMessage.RateLimitError) });
        break;
      case AuthError.MissingCaptcha:
        setIsCaptchaEnabled(true);
        break;
      case AuthError.Unknown:
        setFormError("root", { message: t(m.ErrorMessage.Unknown) });
        break;
    }

    if (isAuthMethodError) {
      setFormError("root", { message: t(m.ErrorMessage.Unknown) });
    }
  }, [loginError, isAuthMethodError, setFormError, t, m]);

  useEffect(() => {
    mutateAuthMethod({
      email: emailHint,
      redirectURL: searchParams.get(SearchParam.RedirectUrl),
    });
  }, [emailHint, mutateAuthMethod, searchParams]);

  const processSignIn = useCallback(
    async (data: SignInFormData) => {
      try {
        if (isAuthMethodSSO) {
          window.location.replace(authMethodData.url);
          return;
        }

        if (!data.password || data.password === "") {
          setFormError("password", { message: t(m.ErrorMessage.InvalidPassword) });
          return;
        }

        if (!isCaptchaEnabled) {
          submit(data.email, data.password);
          return;
        }

        if (!recaptchaRef.current) {
          setFormError("root", { message: t(m.ErrorMessage.CaptchaError) });
          return;
        }

        if (isRecaptchaLoaded) {
          const recaptchaValue = await recaptchaRef.current.execute();

          if (recaptchaValue === "cancel") {
            return;
          }

          if (!recaptchaValue) {
            setFormError("root", { message: t(m.ErrorMessage.CaptchaError) });
            return;
          }

          submit(data.email, data.password, recaptchaValue);
        }
      } catch (error) {
        setFormError("root", { message: t(m.ErrorMessage.CaptchaError) });
        LogSentryException({ messagePrefix: "Captcha error", error });
      }
    },
    [
      authMethodData,
      isAuthMethodSSO,
      isCaptchaEnabled,
      isRecaptchaLoaded,
      setFormError,
      submit,
      t,
      m,
    ]
  );

  useEffect(() => {
    if (
      isCaptchaEnabled &&
      recaptchaRef.current !== null &&
      loginError === AuthError.MissingCaptcha
    ) {
      processSignIn({
        email: getValues("email"),
        password: getValues("password"),
      });
    }
  }, [isCaptchaEnabled, recaptchaRef, processSignIn, loginError, getValues]);

  const onSubmit = async (data: SignInFormData) => {
    clearErrors();
    await processSignIn(data);
  };

  if (isAuthMethodError) {
    return (
      <InfoPage
        variant="warning"
        title={t(m.Login.SomethingWentWrong)}
        primaryCTA={
          <Button onClick={() => navigation.goToSignIn()}>{t(m.Login.BackToLogIn)}</Button>
        }
      />
    );
  }

  return (
    <FormWrapper className={style.signIn}>
      <div className={style.signInForm}>
        <Heading type="h1">{t(m.Login.WelcomeBack)}</Heading>
        {isAuthMethodPassword && (
          <div className={style.joinViewLinkText}>
            <FormattedMessage
              id={m.Login.EnterPasswordToViewLink.id}
              defaultMessage={m.Login.EnterPasswordToViewLink.defaultMessage}
              values={{
                email: <b className={style.emailHint}>{emailHint}</b>,
              }}
            />
          </div>
        )}

        {isAuthMethodSSO && (
          <div className={style.joinViewLinkText}>
            <FormattedMessage
              id={m.Login.LogInWithToViewLink.id}
              defaultMessage={m.Login.LogInWithToViewLink.defaultMessage}
              values={{
                email: <b className={style.emailHint}>{emailHint}</b>,
              }}
            />
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className={style.inputContainer} noValidate>
          {isAuthMethodPassword && (
            <div
              className={classNames(
                style.passwordContainer,
                authMethodData?.method !== AuthMethod.Password && style.hidden
              )}
            >
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    ref={passwordFieldRef}
                    errorMessage={errors.password?.message || errors.root?.message}
                    aria-label="password"
                    type="password"
                    placeholder={t(m.Login.PasswordLabel)}
                    autoComplete="current-password"
                    error={Boolean(errors.password) || Boolean(errors.root)}
                    disabled={inProgress || isSubmitting}
                    tabIndex={isAuthMethodSuccess ? 0 : -1}
                  />
                )}
              />
              <Link className={style.formLink} onClick={() => navigation.goToPasswordReset()}>
                {t(m.Login.ForgotPassword)}
              </Link>
            </div>
          )}

          {isCaptchaEnabled && (
            <ReCAPTCHA ref={recaptchaRef} onLoad={() => setIsRecaptchaLoaded(true)} />
          )}

          <Button
            type="submit"
            loading={inProgress || isSubmitting || isAuthMethodPending}
            aria-label={isAuthMethodSSO ? "continue" : "login"}
          >
            <span>{isAuthMethodSSO ? t(m.Login.Continue) : t(m.Login.LogIn)}</span>
          </Button>
        </form>
      </div>
    </FormWrapper>
  );
};
